import {
  buildPdfMentionProjectHubPath,
  buildPdfCommentPathFromNotification,
  buildProposalOpsReviewPath,
  buildPlanReviewSummaryPath,
  isPdfHubFilesBellNotification,
  buildBoardMentionPath,
  buildClientWorkspacePath,
  buildArchitectPortalDiscussionPath,
  buildProjectHubGroupCommentPath,
  buildContractorPayablesPath,
  buildMarketingContentRequestPath,
  isMarketingContentRequestNotification,
} from './notificationDeepLinks';
import {
  buildHiringCandidatePath,
  hiringResumeViewerPathFromNotification,
  isHiringCandidateNotificationLink,
} from './hiringResumeViewer';
import { purchaseAttachmentViewerPathFromNotification } from './purchasingAttachmentViewer';
import {
  buildSupplierRfqNotificationPath,
  pickHubProjectIdForDeal,
} from './supplierRfqNotificationPath';

export type UserNotificationType =
  | 'new_lead'
  | 'lead_qualified'
  | 'deal_won'
  | 'system'
  | 'new_message'
  | 'team_chat'
  | 'mention'
  | 'task_mention'
  | 'portal_feedback'
  | 'portal_upload'
  | 'portal_deliverable_approval'
  | 'proposal_comment'
  | 'proposal_ops_review_requested'
  | 'proposal_ops_review_approved'
  | 'task_comment'
  | 'architect_comment'
  | 'external_comment'
  | 'pdf_comment'
  | 'pdf_external_comment'
  | 'task_assigned'
  | 'lead_todo_assigned'
  | 'project_file_version'
  | 'project_activity_digest'
  | 'supplier_rfq_upload'
  | 'card_expense_digest'
  | 'task_reminder'
  | 'architect_standup_report'
  | 'board_mention'
  | 'board_comment'
  | 'bug_squashed'
  | 'job_application'
  | 'crew_job_commit'
  | 'contractor_onboard'
  | 'purchase_requisition'
  | 'designer_invoice_review'
  | 'qbo_bill_office'
  | string;

export interface UserNotification {
  id: string;
  type: UserNotificationType;
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  leadId?: string;
  dealId?: string;
  sourceId?: string;
  phoneNumber?: string;
  isPriority?: boolean;
  projectType?: string;
  portalStep?: string;
  projectId?: string;
  taskId?: string;
  leadTodoId?: string;
  firestoreId?: string;
  streamId?: string;
  opsTaskId?: string;
  documentId?: string;
  pdfPageNumber?: number;
  groupCommentKey?: string;
  discussion?: 'architect' | 'comments' | 'pdf';
  /** In-app deep link path (e.g. /card-expenses). */
  link?: string;
}

export function isNotificationClickable(notification: UserNotification): boolean {
  if (notification.leadId) return true;
  if (notification.type === 'new_message') return true;
  if (notification.type === 'team_chat' && notification.link?.startsWith('/team-chat')) return true;
  if (notification.type === 'task_comment' || notification.type === 'architect_comment' || notification.type === 'external_comment') {
    return true;
  }
  if (notification.type === 'pdf_comment' || notification.type === 'pdf_external_comment') return true;
  if (notification.type === 'task_assigned' || notification.type === 'task_reminder' || notification.type === 'task_mention' || notification.type === 'lead_todo_assigned') {
    return true;
  }
  if (notification.type === 'project_file_version' || notification.type === 'supplier_rfq_upload') return true;
  if (notification.type === 'project_activity_digest' && projectActivityDigestPath(notification)) return true;
  if (notification.type === 'plan_review_complete' && notification.projectId) return true;
  if (notification.type === 'mention' && isPdfHubFilesBellNotification(notification)) return true;
  if (notification.opsTaskId) return true;
  if (notification.type === 'deal_won' && notification.dealId) return true;
  if (
    (notification.type === 'proposal_ops_review_requested' || notification.type === 'proposal_ops_review_approved') &&
    notification.dealId
  ) {
    return true;
  }
  if (notification.type === 'portal_feedback' || notification.type === 'portal_upload' || notification.type === 'proposal_comment') {
    return true;
  }
  if (
    notification.type === 'portal_deliverable_approval' &&
    (notification.leadId || notification.link?.startsWith('/client/'))
  ) {
    return true;
  }
  if (notification.type === 'billing_payables' || notification.type === 'capital_plan') return true;
  if (notification.type === 'designer_invoice_review' || notification.type === 'qbo_bill_office') return true;
  if (notification.type === 'purchase_requisition') return true;
  if (notification.type === 'card_expense_digest' && notification.link) return true;
  if (notification.type === 'architect_standup_report' && notification.link) return true;
  if (notification.type === 'portal_walkthrough_summary' && notification.link) return true;
  if (
    (notification.type === 'board_mention' || notification.type === 'board_comment') &&
    notification.leadId &&
    notification.dealId
  ) {
    return true;
  }
  if (notification.groupCommentKey && notification.projectId) return true;
  if (notification.type === 'job_application' || notification.type === 'crew_job_commit' || notification.type === 'contractor_onboard') return true;
  if (isHiringCandidateNotificationLink(notification.link)) return true;
  if (hiringResumeViewerPathFromNotification(notification)) return true;
  return false;
}

export type NotificationNavigateArgs = {
  navigate: (path: string, options?: { state?: Record<string, unknown> }) => void;
  onLeadClick?: (leadId: string, tab?: 'details' | 'activities' | 'tracking') => void;
  onClose?: () => void;
  viewerRole?: string;
};

export function vendorPortalProductionCommentPath(input: {
  dealId?: string;
  groupCommentKey?: string;
  commentId?: string;
}): string {
  const q = new URLSearchParams();
  if (input.dealId?.trim()) q.set('deal', input.dealId.trim());
  if (input.groupCommentKey?.trim()) q.set('group', input.groupCommentKey.trim());
  if (input.commentId?.trim()) q.set('taskComment', input.commentId.trim());
  const qs = q.toString();
  return qs ? `/vendor-portal?${qs}` : '/vendor-portal';
}

/** Prefer Project Hub Production thread (with highlight) when a hub project id is known. */
export function supplierProductionCommentAppPath(input: {
  projectId?: string | null;
  dealId?: string | null;
  groupCommentKey?: string | null;
  commentId?: string | null;
}): string {
  const groupKey = input.groupCommentKey?.trim() || '';
  const commentId = input.commentId?.trim() || '';
  const dealId = input.dealId?.trim() || '';
  const projectId = input.projectId?.trim() || '';
  if (projectId && groupKey) {
    const q = new URLSearchParams();
    q.set('group', groupKey);
    if (commentId) q.set('taskComment', commentId);
    return `/project-hub/${encodeURIComponent(projectId)}?${q.toString()}`;
  }
  return vendorPortalProductionCommentPath({
    dealId,
    groupCommentKey: groupKey || undefined,
    commentId: commentId || undefined,
  });
}

/**
 * Billing alerts open Billing & Payables on the project row.
 * Older bells only stored `/billing-payables`; the source id is the ready-item or bill.
 */
export function billingPayablesNotificationPath(notification: {
  link?: string;
  dealId?: string;
  sourceId?: string;
}): string {
  const link = (notification.link || '').trim();
  const hasTarget = /(?:^|[?&])(deal|bill|item)=/.test(link);
  if (link && hasTarget) return link;
  const params = new URLSearchParams();
  const dealId = (notification.dealId || '').trim();
  const sourceId = (notification.sourceId || '').trim();
  if (dealId) params.set('deal', dealId);
  if (sourceId) params.set('item', sourceId);
  const qs = params.toString();
  if (qs) return `/billing-payables?${qs}`;
  return link || '/billing-payables';
}

function inAppNotificationPath(raw: string | undefined): string | null {
  const link = (raw || '').trim();
  if (!link.startsWith('/') || link.startsWith('//')) return null;
  return link;
}

/**
 * Path for project daily-digest bells (`project_activity_digest`).
 * Uses stored `link` when present; otherwise reconstructs from projectId
 * (and message text for older docs that only say "file versions").
 */
export function projectActivityDigestPath(notification: UserNotification): string | null {
  if (notification.type !== 'project_activity_digest') return null;
  const link = inAppNotificationPath(notification.link);
  if (link) return link;
  const projectId = (notification.projectId || '').trim();
  if (!projectId) return null;
  const blob = `${notification.title || ''} ${notification.message || ''}`;
  if (/file version/i.test(blob) || /plan review/i.test(blob) || /upload/i.test(blob)) {
    return `/project-hub/${projectId}?tab=files`;
  }
  return `/project-hub/${projectId}`;
}

/** Preview iframe for a comment or file mention: the comments popup only. */
export function commentMentionPreviewPath(notification: UserNotification): string | null {
  const projectId = (notification.projectId || '').trim();
  if (!projectId) return null;

  if (isPdfHubFilesBellNotification(notification) && notification.documentId) {
    const q = new URLSearchParams();
    q.set('project', projectId);
    if (notification.groupCommentKey?.trim()) q.set('group', notification.groupCommentKey.trim());
    q.set('discussion', 'pdf');
    q.set('file', notification.documentId);
    if (notification.sourceId?.trim()) q.set('comment', notification.sourceId.trim());
    if (notification.pdfPageNumber != null && !Number.isNaN(Number(notification.pdfPageNumber))) {
      q.set('page', String(notification.pdfPageNumber));
    }
    return `/comment-popout?${q.toString()}`;
  }

  const isCommentMention =
    notification.type === 'task_mention' ||
    (notification.type === 'mention' && !!notification.groupCommentKey?.trim());
  const groupCommentKey = notification.groupCommentKey?.trim() || '';
  if (!isCommentMention || !groupCommentKey) return null;

  const q = new URLSearchParams();
  q.set('project', projectId);
  q.set('group', groupCommentKey);
  if (notification.sourceId?.trim()) q.set('taskComment', notification.sourceId.trim());
  if (notification.discussion) q.set('discussion', notification.discussion);
  return `/comment-popout?${q.toString()}`;
}

function hubTaskPreviewPath(projectId: string, taskId?: string, commentId?: string): string {
  const q = new URLSearchParams();
  if (taskId?.trim()) q.set('task', taskId.trim());
  if (commentId?.trim()) q.set('taskComment', commentId.trim());
  const qs = q.toString();
  return qs ? `/project-hub/${projectId}?${qs}` : `/project-hub/${projectId}`;
}

/**
 * Same-origin path the notification bell can iframe.
 * Comment and quote links include the ids the page already scrolls to.
 * When there is no element to focus, this is the whole destination page.
 */
export function notificationPreviewPath(
  notification: UserNotification,
  viewerRole?: string,
): string | null {
  if (viewerRole === 'supplier' && notification.groupCommentKey) {
    return supplierProductionCommentAppPath({
      projectId: notification.projectId,
      dealId: notification.dealId,
      groupCommentKey: notification.groupCommentKey,
      commentId: notification.sourceId,
    });
  }

  if (notification.type === 'crew_job_commit' || notification.type === 'contractor_onboard') {
    return inAppNotificationPath(notification.link) || '/people/hiring?tab=crew';
  }

  if (notification.type === 'job_application') {
    const applicationId = notification.sourceId?.trim();
    return applicationId
      ? buildHiringCandidatePath({
          applicationId,
          resume: (notification.link || '').includes('resume=1'),
        })
      : (inAppNotificationPath(notification.link) || buildHiringCandidatePath({}));
  }

  const hiringResumePath = hiringResumeViewerPathFromNotification(notification);
  if (hiringResumePath) return hiringResumePath;

  const purchaseAttachmentPath = purchaseAttachmentViewerPathFromNotification(notification);
  if (purchaseAttachmentPath) return purchaseAttachmentPath;

  if (
    (notification.type === 'task_comment' || notification.type === 'task_mention') &&
    isHiringCandidateNotificationLink(notification.link)
  ) {
    return notification.link!;
  }

  const commentPopout = commentMentionPreviewPath(notification);
  if (commentPopout) return commentPopout;

  if (
    (notification.type === 'board_mention' || notification.type === 'board_comment') &&
    notification.leadId &&
    notification.dealId &&
    notification.sourceId
  ) {
    return buildBoardMentionPath({
      leadId: notification.leadId,
      dealId: notification.dealId,
      nodeId: notification.sourceId,
    });
  }

  if (
    (notification.type === 'board_mention' || notification.type === 'board_comment') &&
    notification.link
  ) {
    return inAppNotificationPath(notification.link);
  }

  if (notification.type === 'new_message') return '/messages';

  if (notification.type === 'contractor_invoice') {
    return inAppNotificationPath(notification.link) || buildContractorPayablesPath(notification.sourceId);
  }

  if (isMarketingContentRequestNotification(notification)) {
    return buildMarketingContentRequestPath(notification);
  }

  if (
    (notification.type === 'mention' ||
      notification.type === 'task_mention' ||
      notification.type === 'pdf_comment' ||
      notification.type === 'pdf_external_comment') &&
    isPdfHubFilesBellNotification(notification)
  ) {
    return buildPdfMentionProjectHubPath({
      projectId: notification.projectId!,
      documentId: notification.documentId!,
      annotationId: notification.sourceId!,
      pdfPageNumber: notification.pdfPageNumber,
      streamId: notification.streamId,
    });
  }

  if (notification.opsTaskId) {
    const q = new URLSearchParams();
    q.set('task', notification.opsTaskId);
    const commentId = (notification.sourceId || '').trim();
    if (commentId && commentId !== notification.opsTaskId) q.set('comment', commentId);
    return `/operations?${q.toString()}`;
  }

  if (notification.groupCommentKey && notification.projectId) {
    if (viewerRole === 'architect') {
      return buildArchitectPortalDiscussionPath({
        projectId: notification.projectId,
        commentId: notification.sourceId,
      });
    }
    return buildProjectHubGroupCommentPath({
      projectId: notification.projectId,
      groupCommentKey: notification.groupCommentKey,
      commentId: notification.sourceId,
      discussion: notification.discussion,
    });
  }

  if (
    (notification.type === 'task_mention' ||
      notification.type === 'task_assigned' ||
      notification.type === 'task_reminder' ||
      notification.type === 'task_comment' ||
      notification.type === 'architect_comment' ||
      notification.type === 'external_comment') &&
    notification.projectId
  ) {
    return hubTaskPreviewPath(notification.projectId, notification.taskId, notification.sourceId);
  }

  if (notification.type === 'lead_todo_assigned' && notification.leadId) {
    return buildClientWorkspacePath(notification.leadId, { dealId: notification.dealId });
  }

  if (notification.type === 'project_file_version' && notification.projectId) {
    const q = new URLSearchParams();
    q.set('tab', 'files');
    if (notification.streamId?.trim()) q.set('track', notification.streamId.trim());
    return `/project-hub/${notification.projectId}?${q.toString()}`;
  }

  if (notification.type === 'plan_review_complete' && notification.projectId) {
    const reviewId = notification.sourceId?.trim();
    if (reviewId) {
      return buildPlanReviewSummaryPath({
        projectId: notification.projectId,
        reviewId,
        groupCommentKey: notification.groupCommentKey,
      });
    }
    const q = new URLSearchParams();
    q.set('tab', 'files');
    if (notification.documentId) q.set('file', notification.documentId);
    if (notification.streamId) q.set('track', notification.streamId);
    if (notification.pdfPageNumber != null) q.set('page', String(notification.pdfPageNumber));
    return `/project-hub/${notification.projectId}?${q.toString()}`;
  }

  const pdfPath = buildPdfCommentPathFromNotification(notification);
  if (
    (notification.type === 'pdf_comment' || notification.type === 'pdf_external_comment') &&
    pdfPath
  ) {
    return pdfPath;
  }

  if (
    (notification.type === 'pdf_comment' || notification.type === 'pdf_external_comment') &&
    notification.leadId
  ) {
    return buildClientWorkspacePath(notification.leadId, { dealId: notification.dealId, comms: 'comments' });
  }

  if (notification.type === 'deal_won' && notification.dealId) return '/pipeline';

  if (notification.type === 'supplier_rfq_upload') {
    const link = inAppNotificationPath(notification.link);
    if (link?.startsWith('/project-hub/')) return link;
    return (
      buildSupplierRfqNotificationPath({
        hubProjectId: notification.projectId,
        submissionId: notification.sourceId,
      }) || '/vendor-admin'
    );
  }

  if (notification.type === 'billing_payables') return billingPayablesNotificationPath(notification);
  if (notification.type === 'designer_invoice_review') {
    return inAppNotificationPath(notification.link) || '/finances/project-pl?review=designer-invoices';
  }
  if (notification.type === 'qbo_bill_office') {
    return inAppNotificationPath(notification.link) || '/billing-payables';
  }
  if (notification.type === 'purchase_requisition') {
    return inAppNotificationPath(notification.link) || '/purchasing';
  }
  if (notification.type === 'capital_plan') return inAppNotificationPath(notification.link) || '/finances';
  if (notification.type === 'project_activity_digest') return projectActivityDigestPath(notification);
  if (notification.type === 'card_expense_digest') return inAppNotificationPath(notification.link);
  if (notification.type === 'architect_standup_report') return inAppNotificationPath(notification.link);
  if (notification.type === 'portal_walkthrough_summary') return inAppNotificationPath(notification.link);
  if (notification.type === 'team_chat') return inAppNotificationPath(notification.link);

  if (notification.type === 'portal_deliverable_approval') {
    if (notification.leadId) {
      return buildClientWorkspacePath(notification.leadId, {
        dealId: notification.dealId,
        step: notification.portalStep,
      });
    }
    if (notification.link?.startsWith('/client/')) return notification.link;
  }

  if (
    (notification.type === 'proposal_ops_review_requested' ||
      notification.type === 'proposal_ops_review_approved') &&
    notification.dealId
  ) {
    return buildProposalOpsReviewPath(notification.dealId);
  }

  if (
    notification.leadId &&
    (notification.type === 'portal_feedback' ||
      notification.type === 'portal_upload' ||
      notification.type === 'proposal_comment')
  ) {
    return buildClientWorkspacePath(notification.leadId, {
      dealId: notification.dealId,
      step: notification.portalStep,
    });
  }

  if (notification.leadId && notification.type === 'mention') {
    return buildClientWorkspacePath(notification.leadId, {
      dealId: notification.dealId,
      comms: 'comments',
      commentId: notification.sourceId,
    });
  }

  if (notification.leadId) {
    return buildClientWorkspacePath(notification.leadId, { dealId: notification.dealId });
  }

  return inAppNotificationPath(notification.link);
}

/** Hide the app header inside a notification preview iframe. */
export function withNotificationEmbed(path: string): string {
  const hashAt = path.indexOf('#');
  const hash = hashAt >= 0 ? path.slice(hashAt) : '';
  const withoutHash = hashAt >= 0 ? path.slice(0, hashAt) : path;
  const qAt = withoutHash.indexOf('?');
  const pathname = qAt >= 0 ? withoutHash.slice(0, qAt) : withoutHash;
  const params = new URLSearchParams(qAt >= 0 ? withoutHash.slice(qAt + 1) : '');
  params.set('notifEmbed', '1');
  return `${pathname}?${params.toString()}${hash}`;
}

export async function navigateFromNotification(
  notification: UserNotification,
  { navigate, onLeadClick, onClose, viewerRole }: NotificationNavigateArgs,
): Promise<void> {
  onClose?.();

  if (viewerRole === 'supplier' && notification.groupCommentKey) {
    navigate(
      supplierProductionCommentAppPath({
        projectId: notification.projectId,
        dealId: notification.dealId,
        groupCommentKey: notification.groupCommentKey,
        commentId: notification.sourceId,
      }),
    );
    return;
  }

  if (notification.type === 'crew_job_commit' || notification.type === 'contractor_onboard') {
    navigate(notification.link || '/people/hiring?tab=crew');
    return;
  }

  if (notification.type === 'job_application') {
    const applicationId = notification.sourceId?.trim();
    navigate(
      applicationId
        ? buildHiringCandidatePath({
          applicationId,
          resume: (notification.link || '').includes('resume=1'),
        })
        : (notification.link || buildHiringCandidatePath({})),
    );
    return;
  }

  const hiringResumePath = hiringResumeViewerPathFromNotification(notification);
  if (hiringResumePath) {
    navigate(hiringResumePath);
    return;
  }

  const purchaseAttachmentPath = purchaseAttachmentViewerPathFromNotification(notification);
  if (purchaseAttachmentPath) {
    navigate(purchaseAttachmentPath);
    return;
  }

  if (
    (notification.type === 'task_comment' || notification.type === 'task_mention') &&
    isHiringCandidateNotificationLink(notification.link)
  ) {
    navigate(notification.link!);
    return;
  }

  if (
    (notification.type === 'board_mention' || notification.type === 'board_comment') &&
    notification.leadId &&
    notification.dealId &&
    notification.sourceId
  ) {
    navigate(buildBoardMentionPath({
      leadId: notification.leadId,
      dealId: notification.dealId,
      nodeId: notification.sourceId,
    }));
    return;
  }

  if (
    (notification.type === 'board_mention' || notification.type === 'board_comment') &&
    notification.link
  ) {
    navigate(notification.link);
    return;
  }

  if (notification.type === 'new_message') {
    navigate('/messages', {
      state: {
        selectedLeadId: notification.leadId,
        phoneNumber: notification.phoneNumber,
      },
    });
    return;
  }

  if (notification.type === 'contractor_invoice') {
    navigate(notification.link || buildContractorPayablesPath(notification.sourceId));
    return;
  }

  if (isMarketingContentRequestNotification(notification)) {
    navigate(buildMarketingContentRequestPath(notification));
    return;
  }

  if (
    (notification.type === 'mention' ||
      notification.type === 'task_mention' ||
      notification.type === 'pdf_comment' ||
      notification.type === 'pdf_external_comment') &&
    isPdfHubFilesBellNotification(notification)
  ) {
    navigate(
      buildPdfMentionProjectHubPath({
        projectId: notification.projectId!,
        documentId: notification.documentId!,
        annotationId: notification.sourceId!,
        pdfPageNumber: notification.pdfPageNumber,
        streamId: notification.streamId,
      }),
    );
    return;
  }

  if (notification.opsTaskId) {
    const q = new URLSearchParams();
    q.set('task', notification.opsTaskId);
    const commentId = (notification.sourceId || '').trim();
    if (commentId && commentId !== notification.opsTaskId) {
      q.set('comment', commentId);
    }
    navigate(`/operations?${q.toString()}`);
    return;
  }

  if (notification.groupCommentKey && notification.projectId) {
    if (viewerRole === 'architect') {
      navigate(
        buildArchitectPortalDiscussionPath({
          projectId: notification.projectId,
          commentId: notification.sourceId,
        }),
      );
      return;
    }
    navigate(
      buildProjectHubGroupCommentPath({
        projectId: notification.projectId,
        groupCommentKey: notification.groupCommentKey,
        commentId: notification.sourceId,
        discussion: notification.discussion,
      }),
    );
    return;
  }

  if (notification.type === 'task_mention' && notification.projectId) {
    navigate(`/project-hub/${notification.projectId}`, {
      state: {
        openTaskId: notification.taskId,
        ...(notification.sourceId ? { highlightTaskCommentId: notification.sourceId } : {}),
      },
    });
    return;
  }

  if (notification.type === 'task_assigned' && notification.projectId) {
    navigate(`/project-hub/${notification.projectId}`, { state: { openTaskId: notification.taskId } });
    return;
  }

  if (notification.type === 'task_reminder' && notification.projectId) {
    navigate(`/project-hub/${notification.projectId}`, { state: { openTaskId: notification.taskId } });
    return;
  }

  if (notification.type === 'lead_todo_assigned' && notification.leadId) {
    navigate(buildClientWorkspacePath(notification.leadId, { dealId: notification.dealId }));
    return;
  }

  if (notification.type === 'project_file_version' && notification.projectId) {
    const track = notification.streamId ? `&track=${encodeURIComponent(notification.streamId)}` : '';
    navigate(`/project-hub/${notification.projectId}?tab=files${track}`);
    return;
  }

  const digestPath = projectActivityDigestPath(notification);
  if (digestPath) {
    navigate(digestPath);
    return;
  }

  if (notification.type === 'plan_review_complete' && notification.projectId) {
    const reviewId = notification.sourceId?.trim();
    if (reviewId) {
      navigate(
        buildPlanReviewSummaryPath({
          projectId: notification.projectId,
          reviewId,
          groupCommentKey: notification.groupCommentKey,
        }),
      );
      return;
    }
    const q = new URLSearchParams();
    q.set('tab', 'files');
    if (notification.documentId) q.set('file', notification.documentId);
    if (notification.streamId) q.set('track', notification.streamId);
    if (notification.pdfPageNumber != null) q.set('page', String(notification.pdfPageNumber));
    navigate(`/project-hub/${notification.projectId}?${q.toString()}`);
    return;
  }

  if (
    (notification.type === 'pdf_comment' || notification.type === 'pdf_external_comment') &&
    notification.projectId
  ) {
    const pdfPath = buildPdfCommentPathFromNotification(notification);
    navigate(pdfPath ?? `/project-hub/${notification.projectId}?tab=files`);
    return;
  }

  if (
    (notification.type === 'pdf_comment' || notification.type === 'pdf_external_comment') &&
    notification.leadId
  ) {
    navigate(buildClientWorkspacePath(notification.leadId, { dealId: notification.dealId, comms: 'comments' }));
    return;
  }

  if (notification.type === 'task_comment' || notification.type === 'architect_comment' || notification.type === 'external_comment') {
    if (notification.projectId) {
      navigate(`/project-hub/${notification.projectId}`, {
        state: notification.taskId
          ? {
              openTaskId: notification.taskId,
              ...(notification.sourceId ? { highlightTaskCommentId: notification.sourceId } : {}),
            }
          : undefined,
      });
    } else {
      navigate('/project-hub');
    }
    return;
  }

  if (notification.type === 'deal_won' && notification.dealId) {
    navigate('/pipeline', { state: { selectedDealId: notification.dealId } });
    return;
  }

  if (notification.type === 'supplier_rfq_upload') {
    const link = (notification.link || '').trim();
    if (link.startsWith('/project-hub/')) {
      navigate(link);
      return;
    }
    const fromFields = buildSupplierRfqNotificationPath({
      hubProjectId: notification.projectId,
      submissionId: notification.sourceId,
    });
    if (fromFields) {
      navigate(fromFields);
      return;
    }
    const dealId = (notification.dealId || '').trim();
    if (dealId) {
      try {
        const { collection, getDocs, query, where } = await import('firebase/firestore');
        const { db } = await import('./firebase');
        const snap = await getDocs(
          query(collection(db, 'timberProjects'), where('dealId', '==', dealId)),
        );
        const projects = snap.docs.map((d) => ({
          id: d.id,
          phase: typeof d.data()?.phase === 'string' ? d.data().phase : undefined,
        }));
        const hubId = pickHubProjectIdForDeal(projects);
        const path = buildSupplierRfqNotificationPath({
          hubProjectId: hubId,
          submissionId: notification.sourceId,
        });
        if (path) {
          navigate(path);
          return;
        }
      } catch (err) {
        console.warn('[navigateFromNotification] supplier_rfq_upload hub lookup failed', err);
      }
    }
    navigate('/vendor-admin');
    return;
  }

  if (notification.type === 'billing_payables') {
    navigate(billingPayablesNotificationPath(notification));
    return;
  }

  if (notification.type === 'designer_invoice_review') {
    navigate(notification.link || '/finances/project-pl?review=designer-invoices');
    return;
  }

  if (notification.type === 'qbo_bill_office') {
    navigate(notification.link || '/billing-payables');
    return;
  }

  if (notification.type === 'purchase_requisition') {
    navigate(notification.link || '/purchasing');
    return;
  }

  if (notification.type === 'capital_plan') {
    navigate(notification.link || '/finances');
    return;
  }

  if (notification.type === 'card_expense_digest' && notification.link) {
    navigate(notification.link);
    return;
  }

  if (notification.type === 'architect_standup_report' && notification.link) {
    navigate(notification.link);
    return;
  }

  if (notification.type === 'portal_walkthrough_summary' && notification.link) {
    navigate(notification.link);
    return;
  }

  if (notification.type === 'team_chat' && notification.link?.startsWith('/team-chat')) {
    navigate(notification.link);
    return;
  }

  // Portal deliverable approvals must open the client workspace — never legacy
  // `/client-portals/:id` links (that route does not exist; catch-all → /leads).
  if (notification.type === 'portal_deliverable_approval') {
    if (notification.leadId) {
      navigate(
        buildClientWorkspacePath(notification.leadId, {
          dealId: notification.dealId,
          step: notification.portalStep,
        }),
      );
      return;
    }
    if (notification.link?.startsWith('/client/')) {
      navigate(notification.link);
      return;
    }
  }

  if (
    (notification.type === 'proposal_ops_review_requested' || notification.type === 'proposal_ops_review_approved') &&
    notification.dealId
  ) {
    navigate(buildProposalOpsReviewPath(notification.dealId));
    return;
  }

  if (
    notification.leadId &&
    (notification.type === 'portal_feedback' ||
      notification.type === 'portal_upload' ||
      notification.type === 'deal_won' ||
      notification.type === 'proposal_comment')
  ) {
    navigate(
      buildClientWorkspacePath(notification.leadId, {
        dealId: notification.dealId,
        step: notification.portalStep,
      }),
    );
    return;
  }

  if (notification.leadId) {
    if (notification.type === 'mention') {
      navigate(
        buildClientWorkspacePath(notification.leadId, {
          dealId: notification.dealId,
          comms: 'comments',
          commentId: notification.sourceId,
        }),
      );
    } else if (onLeadClick && !notification.dealId) {
      onLeadClick(notification.leadId);
    } else {
      navigate(buildClientWorkspacePath(notification.leadId, { dealId: notification.dealId }));
    }
  }
}
